import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import API, { formatApiErrorDetail } from "../lib/api";
import CandidateLayout, { StatusPill } from "./CandidateLayout";
import { useAppT } from "../components/home/i18n";
import { CheckCircle2, Circle, ArrowRight, Send, Eye, EyeOff, Clock } from "lucide-react";

export default function CandidateHome() {
  const { a } = useAppT();
  const [profile, setProfile] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try { const { data } = await API.get("/candidate/profile"); setProfile(data); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await API.post("/candidate/profile/submit");
      setProfile(data);
      toast.success(a.candHome.submitted);
    } catch (e) {
      const d = e.response?.data?.detail;
      if (d && d.missing) toast.error(`${a.candHome.missing} ${d.missing.join(", ")}`);
      else toast.error(formatApiErrorDetail(d));
    } finally { setBusy(false); }
  };

  const toggleVisibility = async () => {
    try { const { data } = await API.post("/candidate/profile/visibility", { visible: !profile.visible }); setProfile(data); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  if (!profile) return <CandidateLayout><div className="text-[#5C5855]">{a.candHome.loading}</div></CandidateLayout>;

  const canSubmit = profile.missing.length === 0 && !["submitted", "under_review"].includes(profile.review_status);

  return (
    <CandidateLayout statusBadge={<StatusPill status={profile.status_display} />}>
      <div className="grid gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          <div>
            <h1 className="font-serif text-3xl text-[#173A2C]">{a.candHome.welcome}, {profile.private?.legal_name || a.candHome.welcomeFallback}</h1>
            <p className="mt-1 text-sm text-[#5C5855]">{a.candHome.aliasPre} <b className="text-[#2C2A29]">{profile.alias}</b> {a.candHome.aliasPost}</p>
          </div>

          {profile.review_reason && (
            <div data-testid="review-reason" className="rounded-md border border-[#8B3A3A]/30 bg-[#8B3A3A]/6 p-4">
              <div className="text-sm font-semibold text-[#8B3A3A]">{a.candHome.changesTitle}</div>
              <p className="mt-1 text-sm text-[#5C5855]">{profile.review_reason}</p>
            </div>
          )}

          {profile.has_pending_changes && profile.approved && (
            <div className="flex items-start gap-2 rounded-md border border-[#D28E3D]/40 bg-[#D28E3D]/10 p-4">
              <Clock className="mt-0.5 h-4 w-4 text-[#b0722a]" />
              <p className="text-sm text-[#8a5a1f]">{a.candHome.pendingEdits}
                {["submitted", "under_review"].includes(profile.review_status) ? "" : a.candHome.pendingSubmitHint}</p>
            </div>
          )}

          <div className="rounded-md border border-[#E8E4DB] bg-white p-6">
            <div className="flex items-center justify-between">
              <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[#C4A46B]">{a.candHome.completeness}</div>
              <span data-testid="completeness-pct" className="text-lg font-bold text-[#1F4A38]">{profile.completeness}%</span>
            </div>
            <div className="mt-2 h-2 w-full rounded-full bg-[#E8E4DB]"><div className="h-2 rounded-full bg-[#1F4A38] transition-all" style={{ width: `${profile.completeness}%` }} /></div>
            <div className="mt-4 grid gap-2 sm:grid-cols-2">
              {profile.checklist.map((c) => (
                <div key={c.key} data-testid={`check-${c.key}`} className="flex items-center gap-2 text-sm text-[#2C2A29]">
                  {c.done ? <CheckCircle2 className="h-4 w-4 text-[#2E6B4B]" /> : <Circle className="h-4 w-4 text-[#D28E3D]" />}
                  <span className={c.done ? "" : "text-[#5C5855]"}>{a.checklist[c.key] || c.label}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="flex flex-wrap gap-3">
            <button data-testid="submit-profile-btn" onClick={submit} disabled={!canSubmit || busy}
              className="inline-flex items-center gap-2 rounded-full bg-[#1F4A38] px-5 py-2.5 text-sm font-semibold text-white disabled:opacity-50">
              <Send className="h-4 w-4" /> {profile.approved ? a.candHome.submitChanges : a.candHome.submitReview}
            </button>
            {profile.approved && (
              <button data-testid="visibility-btn" onClick={toggleVisibility}
                className="inline-flex items-center gap-2 rounded-full border border-[#E8E4DB] px-5 py-2.5 text-sm text-[#2C2A29]">
                {profile.visible ? <><EyeOff className="h-4 w-4" /> {a.candHome.withdraw}</> : <><Eye className="h-4 w-4" /> {a.candHome.makeVisible}</>}
              </button>
            )}
          </div>
          {!canSubmit && profile.missing.length > 0 && (
            <p className="text-xs text-[#8B3A3A]">{a.candHome.completeFirst} {profile.checklist.filter((c) => !c.done).map((c) => a.checklist[c.key] || c.label).join(", ")}</p>
          )}
        </div>

        <div className="space-y-4">
          <Link to="/candidate/profile" data-testid="go-profile" className="block rounded-md border border-[#E8E4DB] bg-white p-5 transition-colors hover:border-[#1F4A38]">
            <div className="flex items-center justify-between"><span className="font-serif text-lg text-[#173A2C]">{a.candHome.editProfile}</span><ArrowRight className="h-4 w-4 text-[#C4A46B]" /></div>
            <p className="mt-1 text-sm text-[#5C5855]">{a.candHome.editProfileSub}</p>
          </Link>
          <Link to="/candidate/media" data-testid="go-media" className="block rounded-md border border-[#E8E4DB] bg-white p-5 transition-colors hover:border-[#1F4A38]">
            <div className="flex items-center justify-between"><span className="font-serif text-lg text-[#173A2C]">{a.candHome.mediaTitle}</span><ArrowRight className="h-4 w-4 text-[#C4A46B]" /></div>
            <p className="mt-1 text-sm text-[#5C5855]">{a.candHome.mediaSub}</p>
          </Link>
        </div>
      </div>
    </CandidateLayout>
  );
}
